import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import { OPEN_BUG_REPORT_EVENT } from '../../../shared/diagnostics/ErrorBoundary';
import { BugReportDialog } from '../ui/BugReportDialog';

export type BugReportOpenRequest = {
  source: 'dock' | 'assistant' | 'portal';
  initialKind?: 'bug' | 'improvement' | 'support' | 'anonymous';
  conversationId?: number | null;
  planId?: number | null;
  nonce: number;
};

type BugReportContextValue = {
  openBugReport: (opts?: {
    source?: 'dock' | 'assistant' | 'portal';
    initialKind?: 'bug' | 'improvement' | 'support' | 'anonymous';
    conversationId?: number | null;
    planId?: number | null;
  }) => void;
};

const BugReportContext = createContext<BugReportContextValue | null>(null);

const NOOP_BUG_REPORT: BugReportContextValue = { openBugReport: () => {} };

export function useBugReport(): BugReportContextValue {
  return useContext(BugReportContext) ?? NOOP_BUG_REPORT;
}

/** Провайдер держит одно окно баг-репорта на всё приложение (кнопка ⚠, шапка ассистента, ErrorBoundary). */
export function BugReportProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<BugReportOpenRequest | null>(null);

  const openBugReport = useCallback<BugReportContextValue['openBugReport']>((opts) => {
    setRequest({
      source: opts?.source ?? 'dock',
      initialKind: opts?.initialKind ?? 'bug',
      conversationId: opts?.conversationId ?? null,
      planId: opts?.planId ?? null,
      nonce: Date.now()
    });
  }, []);

  useEffect(() => {
    const onOpen = () => openBugReport({ source: 'dock' });
    window.addEventListener(OPEN_BUG_REPORT_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_BUG_REPORT_EVENT, onOpen);
  }, [openBugReport]);

  const value = useMemo(() => ({ openBugReport }), [openBugReport]);

  return (
    <BugReportContext.Provider value={value}>
      {children}
      <BugReportDialog open={request != null} request={request} onClose={() => setRequest(null)} />
    </BugReportContext.Provider>
  );
}
